/**
 * First match wins — the arms an earlier arm that always answers shadows
 * (D245, D246).
 *
 * @remarks
 * Several of a pack's lists are read first match wins: an item's `use` arms
 * (among the arms aimed where the player aimed), and a `choose` effect's arms
 * (D246). In every such list an arm that always answers — no conditions, no
 * `when` — ends the reading for its aim, so an arm after it aimed the same
 * way can never run: its lines and effects sit in the JSON and nobody meets
 * them. The rule is one idea, so it is written once, here in the lowest
 * package, where the schema (the choose) and the loader (the use arms) both
 * reach it. Each caller says what "always answers" means for its arms and,
 * when its arms are aimed, what the aim is.
 *
 * @packageDocumentation
 */

/** One arm that can never run, and the aim an earlier arm already answers. */
export interface ShadowedArm {
	readonly aim: string;
	readonly armIndex: number;
}

/** The aim of a list whose arms are not aimed: every arm answers the same question. */
const ONE_AIM = (): string => 'every reading';

/**
 * Every arm an earlier always-answering arm aimed the same way shadows.
 *
 * @param arms - The list, in reading order
 * @param alwaysAnswers - Whether an arm answers whatever the world is doing (no conditions)
 * @param aimOf - What an arm answers — a target, an exit, nothing; one aim for a list that is not aimed
 * @returns The shadowed arms, in reading order
 */
export const shadowedArmsOf = <Arm>(
	arms: readonly Arm[],
	alwaysAnswers: (arm: Arm) => boolean,
	aimOf: (arm: Arm) => string = ONE_AIM,
): readonly ShadowedArm[] => {
	const answered = new Set<string>();

	return arms.flatMap((arm, armIndex) => {
		const aim = aimOf(arm);
		const shadowed = answered.has(aim) ? [{ aim, armIndex }] : [];

		if (alwaysAnswers(arm)) {
			answered.add(aim);
		}

		return shadowed;
	});
};
